import moment from "moment";
import { filter, find, sortBy } from "lodash";
import { AppShape, BorrowerEntity, DocShape, DocTypes } from "@eave/shared/types/api";
import { warnMovedToShared } from "@eave/shared/utils/deprecation";
import { isProduction } from "config";
import { addressToStr as addressToString } from "@eave/shared/utils/addresses";

const humanize = require("underscore.string/humanize");
const titleize = require("underscore.string/titleize");

/* interfaces */

/* constants */

export const otherIncomeTypes = [
  "Social Security",
  "Pension",
  "IRA Distribution",
  "IRA/401(k)",
  "Child Support",
  "Alimony",
  "Note Receivable",
  "Military Pension",
  "Military Disability",
];

export const baseIncomeCalculationMethods = [
  "base_pay_rate_calc",
  "current_base_earnings_calc",
  "ytd_monthly_average",
  "ytd_and_prior_year_average",
  "ytd_and_prior_two_years_average",
  "prior_two_years_average",
];

export const additionalIncomeCalculationMethods = [
  "ytd_monthly_average",
  "ytd_and_prior_year_average",
  "ytd_and_prior_two_years_average",
  "prior_two_years_average",
  "prior_year_average",
  "ytd_annualized",
  "ytd_annualized_and_prior_year_average",
  "ytd_annualized_and_prior_two_years_average",
];

export const selectableCOECategories = [
  { label: "Seller Performance", value: "seller_performance" },
  { label: "Seller Performance (New Construction)", value: "seller_performance_new_construction" },
  { label: "Buyer Performance", value: "buyer_performance" },
  { label: "HLHL Performance (Appraisal)", value: "hlhl_performance_appraisal" },
  { label: "HLHL Performance (TRID)", value: "hlhl_performance_trid" },
  { label: "HLHL Performance (RESPA Data Error)", value: "hlhl_performance_respa_data_error" },
  {
    label: "HLHL Performance (Operational Breakdown)",
    value: "hlhl_performance_operational_breakdown",
  },
  { label: "HLH Performance (Trade-In)", value: "hlh_performance_trade_in" },
  { label: "HLH Performance (Cash Offer)", value: "hlh_performance_cash_offer" },
  { label: "Non-HL Title Escrow Performance", value: "non_hl_title_escrow_performance" },
  { label: "HL Title Escrow Performance", value: "hl_title_escrow_performance" },
];

export const selectableDocTypes = [
  DocTypes.TenOThreeFinal,
  DocTypes.TenOThreeInitial,
  DocTypes.TenOEightAndUnderwritingSummary,
  DocTypes.TenOEightFinal,
  DocTypes.TenNinetyNine,
  DocTypes.FortyFiveOSixTInitial,
  DocTypes.FortyFiveOSixCInitial,
  DocTypes.AccurateGroupResponse,
  DocTypes.AcknowledgementOfAppraisalReceipt,
  DocTypes.AcknowledgementOfCfpbBooklets,
  DocTypes.AdverseActionNotice,
  DocTypes.Allonge,
  DocTypes.Appraisal_1004d,
  DocTypes.Appraisal1004dInvoice,
  DocTypes.AppraisalDeskReview,
  DocTypes.AppraisalFieldReview,
  DocTypes.AppraisalInvoice,
  DocTypes.AppraisalMismo,
  DocTypes.AppraisalReport,
  DocTypes.AppraisalSSRFannie,
  DocTypes.AppraisalSSRFreddie,
  DocTypes.AssetOther,
  DocTypes.AssetReport,
  DocTypes.Statement, // between AssetOther and AssetSummary since display text is "Asset Statement"
  DocTypes.AssetSummary,
  DocTypes.BalanceSheet,
  DocTypes.BankruptcyDocuments,
  DocTypes.BirthCertificate,
  DocTypes.BorrowersAuthorization,
  DocTypes.CharmBooklet,
  DocTypes.CpaLetterBusinessLicense,
  DocTypes.ChildSupportAlimonyDebt,
  DocTypes.ChildSupportAlimonyIncome,
  DocTypes.ClosingPackage,
  DocTypes.ClosingProtectionLetter,
  DocTypes.ConditionalApprovalLetter,
  DocTypes.ConditionAttachment,
  DocTypes.CorrespondenceBorrower,
  DocTypes.CorrespondenceVendor_3rdPartyProvider,
  DocTypes.CreditOther,
  DocTypes.CreditReport,
  DocTypes.CreditSupplement,
  DocTypes.CustodianTape,
  DocTypes.DesktopUnderwriterFindings,
  DocTypes.DesktopUnderwriterStatusLog,
  DocTypes.DesktopUnderwriterFindingsXML,
  DocTypes.DeathCertificate,
  DocTypes.DownpaymentAssistanceLetter,
  DocTypes.EarnestMoneyDeposit,
  DocTypes.EarthquakePolicy,
  DocTypes.ExplanationAttachment,
  DocTypes.FinalClosingDisclosure,
  DocTypes.FinalTitlePolicy,
  DocTypes.FirstTimeHomebuyerEducationCertificate,
  DocTypes.FloodCertificate,
  DocTypes.FloodPolicy,
  DocTypes.FundingTape,
  DocTypes.FNMA32,
  DocTypes.GiftFundsEvidence,
  DocTypes.GiftLetter,
  DocTypes.GiftLetterTemplate,
  DocTypes.GoodbyeLetter,
  DocTypes.Ho6InsurancePolicy,
  DocTypes.HoaCondoDocs,
  DocTypes.HoaDues,
  DocTypes.HoaMasterFloodInsurancePolicy,
  DocTypes.HoaMasterHomeownersInsurancePolicy,
  DocTypes.HOITransferOfServicing,
  DocTypes.HomeInspectionReport,
  DocTypes.HomeLoanToolkitBooklet,
  DocTypes.HomeownersPolicy,
  DocTypes.Income,
  DocTypes.IncomeOther,
  DocTypes.IncomeMilitary,
  DocTypes.IncomeMilitaryResidual,
  DocTypes.IncomeSelfEmployed,
  DocTypes.IncomeSummary,
  DocTypes.InitialClosingDisclosure,
  DocTypes.InitialDisclosurePackage,
  DocTypes.InitialLoanEstimate,
  DocTypes.InvestorExceptionVarianceApproval,
  DocTypes.InvestorLoanApproval,
  DocTypes.InvestorPostclosingPackage,
  DocTypes.InvestorPrefundingPackage,
  DocTypes.InvestorRateLock,
  DocTypes.InvestorSuspenseConditions,
  DocTypes.K1,
  DocTypes.LawsuitJudgment,
  DocTypes.LetterOfExplanationAsset,
  DocTypes.LetterOfExplanationCredit,
  DocTypes.LetterOfExplanationIncome,
  DocTypes.LoanDetailReport,
  DocTypes.LoansafeAppraisalManagerReport,
  DocTypes.LoansafeAppraisalManagerXml,
  DocTypes.LoansafeFraudManagerReport,
  DocTypes.LoansafeFraudManagerXml,
  DocTypes.MersRegistration,
  DocTypes.MersTransfer,
  DocTypes.ManuallyEditedDisclosure,
  DocTypes.MarriageLicense,
  DocTypes.MessageAttachment,
  DocTypes.MortgageInsuranceCertificate,
  DocTypes.MortgageInsuranceQuote,
  DocTypes.MortgageInsuranceStatementOfDenial,
  DocTypes.MortgageStatement,
  DocTypes.Unused,
  DocTypes.NonPermanentResidentVisa,
  DocTypes.Note,
  DocTypes.NoticeOfIncompleteApplication,
  DocTypes.OriginWarehouseTape,
  DocTypes.PaymentFromBorrower,
  DocTypes.PaymentHistoryStatement,
  DocTypes.PayoffDemandStatement,
  DocTypes.Paystub,
  DocTypes.PermanentResidentCardVisa,
  DocTypes.PestInspection,
  DocTypes.PhotoIdentification,
  DocTypes.PlaidTransactionHistory,
  DocTypes.PostClosingPayoffStatement,
  DocTypes.PowerOfAttorney,
  DocTypes.PreAppLoanEstimate,
  DocTypes.PrequalPackageAuditTrail,
  DocTypes.PrequalPackageCertificate,
  DocTypes.ProfitAndLossStatement,
  DocTypes.PurchaseAdvice,
  DocTypes.PurchaseContract,
  DocTypes.ReceiptOfFundsToClose,
  DocTypes.ReoAvm,
  DocTypes.ReoEstimatedSettlementStatement,
  DocTypes.ReoExpenses,
  DocTypes.ReoFinalSettlementStatement,
  DocTypes.ReoPendingSalePropertyContract,
  DocTypes.RateLockAgreement,
  DocTypes.RateLockAgreementCertificate,
  DocTypes.RateLockTape,
  DocTypes.RecordedSecurityInstrument,
  DocTypes.RelocationAgreement,
  DocTypes.RentalReoLease,
  DocTypes.RevisedLoanEstimate,
  DocTypes.Ssa89,
  DocTypes.SSNVerification,
  DocTypes.SatisfactionOfJudgementLienLawsuit,
  DocTypes.SecurityInstrument,
  DocTypes.ServiceLinkResponse,
  DocTypes.ServicingTape,
  DocTypes.SettlementStatementEstimated,
  DocTypes.SettlementStatementFinal,
  DocTypes.ShippingLabel,
  DocTypes.SocialSecurityCard,
  DocTypes.SubjectPropertyAvm,
  DocTypes.SubjectPropertyOther,
  DocTypes.SubjectPropertyReport,
  DocTypes.TaxCertificate,
  DocTypes.TaxReturn1065Partnership,
  DocTypes.TaxReturn1120CCorp,
  DocTypes.TaxReturn1120sSCorp,
  DocTypes.TaxReturnTranscript1065Partnership,
  DocTypes.TaxReturnTranscript1120CCorp,
  DocTypes.TaxReturnTranscript1120sSCorp,
  DocTypes.TaxTranscript,
  DocTypes.TitleCommitment,
  DocTypes.TitleEO,
  DocTypes.TitleOther,
  DocTypes.TrustAgreementOrCertification,
  DocTypes.UCDFannie,
  DocTypes.UCDFreddie,
  DocTypes.ULIRegistration,
  DocTypes.UnderwritingLoanApproval,
  DocTypes.UnderwritingSummary,
  DocTypes.Unknown,
  DocTypes.VACertificateOfEligibility,
  DocTypes.VerbalVerificationOfEmployment,
  DocTypes.VerificationOfMortgage,
  DocTypes.VerificationOfRent,
  DocTypes.W2,
  DocTypes.W2Transcript,
  DocTypes.W9,
  DocTypes.WindAndHailPolicy,
  DocTypes.WireInstructions,
  DocTypes.WorknumberVerbalEmploymentVerification,
  DocTypes.WorknumberEmploymentVerification,
  DocTypes.WrittenVerificationOfEmployment,
  DocTypes.WvoeCertificate,
  DocTypes.IncomeTaxReturn2013,
  DocTypes.IncomeTaxReturn2014,
  DocTypes.IncomeTaxReturn2015,
  DocTypes.IncomeTaxReturn2016,
  DocTypes.IncomeTaxReturn2017,
  DocTypes.IncomeTaxReturn2018,
  DocTypes.IncomeTaxReturn2019,
  DocTypes.IncomeTaxReturn2020,
  DocTypes.IncomeTaxReturn2021,
  DocTypes.ChildCareLetter,
  DocTypes.NPSCreditReport,
];

export const selectableDocOwnerTypes = [
  "account",
  "employment",
  "additional_income",
  "owned_real_estate",
];

export const paystubBasePayFieldPrefixes = [
  "base_pay",
  "vacation_pay",
  "pto_pay",
  "sick_pay",
  "additional_base_pay_1",
  "additional_base_pay_2",
];

export const paystubNonBasePayFieldPrefixes = [
  "bonus_pay",
  "commission_pay",
  "additional_variable_pay_1",
  "additional_variable_pay_2",
  "overtime_pay",
];

/* documents */

export const getDocById = (docs: DocShape[], docId: number) => {
  if (!docs || !docs.length) {
    return null;
  }
  return find(docs, { id: docId });
};

export const getDocsOfType = (docs: DocShape[], docType: string) => {
  return filter(docs, (d: DocShape) => d.type === docType);
};

export const getSortedDocs = (docs: DocShape[]) => {
  return sortBy(docs, (d: DocShape) => +moment(d.created_at)); // oldest first
};

export const getSortedDocsOfType = (docs: DocShape[], docType: string) => {
  if (!docs || !docs.length) {
    return null;
  }
  return getSortedDocs(getDocsOfType(docs, docType));
};

export const getOldestDocOfType = (documents: DocShape[], docType: string) => {
  const docs = getSortedDocsOfType(documents, docType);
  if (!docs || !docs.length) {
    return null;
  }
  return docs[0];
};

const getLatestCreatedDocOfTypeFromDocuments = (documents: DocShape[], docType: string) => {
  const docs = getSortedDocsOfType(documents, docType);
  if (!docs || !docs.length) {
    return null;
  }
  return docs[docs.length - 1];
};

export const getLatestCreatedDocOfType = (app: AppShape, docType: string) =>
  getLatestCreatedDocOfTypeFromDocuments(app.documents, docType);

// The Allonge letter is created as unused and it must be displayed in the PostClosingDocs hurdle
export const getLatestCreatedDocOfTypeIncludingUnusedDocs = (app: AppShape, docType: string) =>
  getLatestCreatedDocOfTypeFromDocuments(app.all_documents, docType);

export const findFieldValueForSection = (doc, fieldKey, sectionKey) => {
  for (let i = 0; i < doc.sections.length; i += 1) {
    const documentSection = doc.sections[i];
    if (documentSection.section_key !== sectionKey) {
      continue;
    }
    for (let j = 0; j < documentSection.fields.length; j += 1) {
      const documentField = documentSection.fields[j];
      if (documentField.field_key === fieldKey) {
        return documentField.field_value;
      }
    }
  }
  return "";
};

export const findFieldValue = (doc, fieldKey) => {
  for (let i = 0; i < doc.sections.length; i += 1) {
    for (let j = 0; j < doc.sections[i].fields.length; j += 1) {
      const documentField = doc.sections[i].fields[j];
      if (documentField.field_key === fieldKey) {
        return documentField.field_value;
      }
    }
  }
  return "";
};

/* rendering */

export const appToBorrower1Name = (app): string => {
  const { borrowers, borrower1_first_name, borrower1_last_name } = app;

  if (!borrowers || !borrowers[1] || !borrowers[1].first_name) {
    if (!borrower1_first_name && !borrower1_last_name) {
      return "";
    }
    return `${borrower1_first_name} ${borrower1_last_name}`;
  }
  return titleize(`${borrowers[1].first_name} ${borrowers[1].last_name}`);
};

export const appToBorrower2Name = (app): string => {
  const { borrowers, borrower2_first_name, borrower2_last_name } = app;

  if (!borrowers || !borrowers[2] || !borrowers[2].first_name) {
    if (!borrower2_first_name && !borrower2_last_name) {
      return "";
    }
    return `${borrower2_first_name} ${borrower2_last_name}`;
  }
  return titleize(`${borrowers[2].first_name} ${borrowers[2].last_name}`);
};

export const appToBorrower3Name = (app): string => {
  const { borrowers, borrower3_first_name, borrower3_last_name } = app;

  if (!borrowers || !borrowers[4] || !borrowers[4].first_name) {
    if (!borrower3_first_name && !borrower3_last_name) {
      return "";
    }
    return `${borrower3_first_name} ${borrower3_last_name}`;
  }
  return titleize(`${borrowers[4].first_name} ${borrowers[4].last_name}`);
};

export const appToBorrower4Name = (app): string => {
  const { borrowers, borrower4_first_name, borrower4_last_name } = app;

  if (!borrowers || !borrowers[8] || !borrowers[8].first_name) {
    if (!borrower4_first_name && !borrower4_last_name) {
      return "";
    }
    return `${borrower4_first_name} ${borrower4_last_name}`;
  }
  return titleize(`${borrowers[8].first_name} ${borrowers[8].last_name}`);
};

export const appToBorrowerName = (app: AppShape, entity: BorrowerEntity): string => {
  const { borrowers } = app;
  const b = borrowers[entity];
  if (!b) {
    return "";
  }

  return titleize(`${b.first_name} ${b.last_name}`);
};

export const appToBorrowerNames = (app): string => {
  const name1 = appToBorrowerName(app, BorrowerEntity.Borrower1);
  const name2 = appToBorrowerName(app, BorrowerEntity.Borrower2);
  const name3 = appToBorrowerName(app, BorrowerEntity.Borrower3);
  const name4 = appToBorrowerName(app, BorrowerEntity.Borrower4);

  if (name1 === "" && name2 === "") {
    return "";
  }
  if (name2 === "") {
    return name1;
  }
  if (name3 === "") {
    return `${name1} & ${name2}`;
  }
  if (name4 === "") {
    return `${name1}, ${name2}, & ${name3}`;
  }
  return `${name1}, ${name2}, ${name3}, & ${name4}`;
};

export const addressToStr = (line1, line2, city, state, zip): string => {
  warnMovedToShared("addressToStr", "/utils/addresses");
  if (!city || !state) return "";

  let address = `${titleize(city)}, ${state}`;
  if (line1 && line2 && zip) {
    address = `${titleize(line1)}, ${titleize(line2)}, ${address} ${zip}`;
  } else if (line1 && zip) {
    address = `${titleize(line1)}, ${address} ${zip}`;
  }
  return address;
};

export const appToPropertyAddress = (app): string =>
  addressToString(
    (app.computed_properties.purchased_property_address &&
      app.computed_properties.purchased_property_address.line1) ||
      "",
    (app.computed_properties.purchased_property_address &&
      app.computed_properties.purchased_property_address.line2) ||
      "",
    (app.computed_properties.purchased_property_address &&
      app.computed_properties.purchased_property_address.city) ||
      "",
    (app.computed_properties.purchased_property_address &&
      app.computed_properties.purchased_property_address.state) ||
      "",
    (app.computed_properties.purchased_property_address &&
      app.computed_properties.purchased_property_address.postal_code) ||
      "",
  );

export const salesAppURL = () => {
  if (isProduction()) {
    return "https://sales-app.homelight.com";
  }

  return "https://staging.sales-app.homelight.com";
};

export const docToHumanStr = (doc: DocShape) => {
  let m = moment(doc.created_at);

  // FIXME: jjw: when re-requesting flood certs, PDF doc is updated in place;
  // to show that the doc has been refreshed, we display updated_at instead of
  // created_at (we're not yet changing this more generally, because operators
  // sometimes edit doc owner entities over the life of a loan app, so we don't
  // want to accidentally cause sort instability with other docs in the system)
  if (doc.type === DocTypes.FloodCertificate) {
    m = moment(doc.updated_at);
  }

  const date = m.format("YYYY-MM-DD");
  const time = m.format("h:mm:ss a");

  return `[${date} / ${time}] ${doc.filename}`;
};

export const docOwnertoHumanStr = owner => humanize(owner);

export const docTypeToCategory = t => {
  const categoryAccounting = "Accounting";
  const categoryAsset = "Asset";
  const categoryClosing = "Closing";
  const categoryCredit = "Credit";
  const categoryDisclosures = "Disclosures";
  const categoryIncome = "Income";
  const categoryInvestor = "Investor";
  const categoryLoanDecision = "Loan Decision";
  const categoryMisc = "Misc";
  const categoryPostClosing = "Post Closing";
  const categorySubjectProperty = "Subject Property";
  const categorySystemNotifications = "System Notifications";

  const map = {
    [DocTypes.TenOThreeFinal]: categoryClosing,
    [DocTypes.TenOThreeInitial]: categoryDisclosures,
    [DocTypes.TenOEightAndUnderwritingSummary]: categoryLoanDecision,
    [DocTypes.FortyFiveOSixTInitial]: categoryDisclosures,
    [DocTypes.FortyFiveOSixCInitial]: categoryDisclosures,
    [DocTypes.AcknowledgementOfAppraisalReceipt]: categoryMisc,
    [DocTypes.AppraisalMismo]: categorySubjectProperty,
    [DocTypes.FundingTape]: categoryClosing,
    [DocTypes.PrequalPackageAuditTrail]: categoryMisc,
    [DocTypes.PrequalPackageCertificate]: categoryMisc,
    [DocTypes.TenOEightFinal]: categoryLoanDecision,
    [DocTypes.TenNinetyNine]: categoryIncome,
    [DocTypes.AccurateGroupResponse]: categorySystemNotifications,
    [DocTypes.AcknowledgementOfCfpbBooklets]: categoryDisclosures,
    [DocTypes.AdverseActionNotice]: categoryDisclosures,
    [DocTypes.Allonge]: categoryPostClosing,
    [DocTypes.Appraisal_1004d]: categorySubjectProperty,
    [DocTypes.Appraisal1004dInvoice]: categoryAccounting,
    [DocTypes.AppraisalDeskReview]: categorySubjectProperty,
    [DocTypes.AppraisalFieldReview]: categorySubjectProperty,
    [DocTypes.AppraisalInvoice]: categoryAccounting,
    [DocTypes.AppraisalReport]: categorySubjectProperty,
    [DocTypes.AppraisalSSRFannie]: categorySubjectProperty,
    [DocTypes.AppraisalSSRFreddie]: categorySubjectProperty,
    [DocTypes.AssetOther]: categoryAsset,
    [DocTypes.AssetReport]: categoryAsset,
    [DocTypes.AssetSummary]: categoryAsset,
    [DocTypes.BalanceSheet]: categoryIncome,
    [DocTypes.BankUnitedWarehouseFundingTape]: categoryClosing,
    [DocTypes.BankruptcyDocuments]: categoryCredit,
    [DocTypes.BirthCertificate]: categoryIncome,
    [DocTypes.BorrowersAuthorization]: categoryDisclosures,
    [DocTypes.CharmBooklet]: categoryDisclosures,
    [DocTypes.ChildSupportAlimonyDebt]: categoryCredit,
    [DocTypes.ChildSupportAlimonyIncome]: categoryIncome,
    [DocTypes.ClosingPackage]: categoryClosing,
    [DocTypes.ClosingProtectionLetter]: categoryClosing,
    [DocTypes.ConditionalApprovalLetter]: categoryMisc,
    [DocTypes.ConditionAttachment]: categoryMisc,
    [DocTypes.CorrespondenceBorrower]: categoryMisc,
    [DocTypes.CorrespondenceVendor_3rdPartyProvider]: categoryMisc,
    [DocTypes.CpaLetterBusinessLicense]: categoryIncome,
    [DocTypes.CreditOther]: categoryCredit,
    [DocTypes.CreditReport]: categoryCredit,
    [DocTypes.CreditSupplement]: categoryCredit,
    [DocTypes.CustodianTape]: categoryPostClosing,
    [DocTypes.DeathCertificate]: categoryMisc,
    [DocTypes.DesktopUnderwriterFindings]: categoryLoanDecision,
    [DocTypes.DesktopUnderwriterStatusLog]: categoryLoanDecision,
    [DocTypes.DesktopUnderwriterFindingsXML]: categoryLoanDecision,
    [DocTypes.DownpaymentAssistanceLetter]: categoryAsset,
    [DocTypes.EarnestMoneyDeposit]: categoryAsset,
    [DocTypes.EarthquakePolicy]: categorySubjectProperty,
    [DocTypes.ExplanationAttachment]: categoryMisc,
    [DocTypes.FNMA32]: categoryLoanDecision,
    [DocTypes.FinalClosingDisclosure]: categoryClosing,
    [DocTypes.FinalTitlePolicy]: categoryPostClosing,
    [DocTypes.FirstTimeHomebuyerEducationCertificate]: categoryDisclosures,
    [DocTypes.FloodCertificate]: categorySubjectProperty,
    [DocTypes.FloodPolicy]: categorySubjectProperty,
    [DocTypes.GiftFundsEvidence]: categoryAsset,
    [DocTypes.GiftLetter]: categoryAsset,
    [DocTypes.GiftLetterTemplate]: categoryAsset,
    [DocTypes.GoodbyeLetter]: categoryPostClosing,

    // NOTE: jjw: GoodbyeLetterTransferOfServicing no longer selectable in UI; superseded by:
    // -- GoodbyeLetter
    // -- HOITransferOfServicing
    // type remains here until we choose to migrate older documents to two new types
    [DocTypes.GoodbyeLetterTransferOfServicing]: categoryPostClosing,

    [DocTypes.Ho6InsurancePolicy]: categorySubjectProperty,
    [DocTypes.HoaCondoDocs]: categorySubjectProperty,
    [DocTypes.HoaDues]: categorySubjectProperty,
    [DocTypes.HoaMasterFloodInsurancePolicy]: categorySubjectProperty,
    [DocTypes.HoaMasterHomeownersInsurancePolicy]: categorySubjectProperty,
    [DocTypes.HOITransferOfServicing]: categoryPostClosing,
    [DocTypes.HomeInspectionReport]: categorySubjectProperty,
    [DocTypes.HomeLoanToolkitBooklet]: categoryDisclosures,
    [DocTypes.HomeownersPolicy]: categorySubjectProperty,
    [DocTypes.Income]: categoryIncome,
    [DocTypes.IncomeOther]: categoryIncome,
    [DocTypes.IncomeMilitary]: categoryIncome,
    [DocTypes.IncomeMilitaryResidual]: categoryIncome,
    [DocTypes.IncomeSelfEmployed]: categoryIncome,
    [DocTypes.IncomeSummary]: categoryIncome,
    [DocTypes.IncomeTaxReturn2013]: categoryIncome,
    [DocTypes.IncomeTaxReturn2014]: categoryIncome,
    [DocTypes.IncomeTaxReturn2015]: categoryIncome,
    [DocTypes.IncomeTaxReturn2016]: categoryIncome,
    [DocTypes.IncomeTaxReturn2017]: categoryIncome,
    [DocTypes.IncomeTaxReturn2018]: categoryIncome,
    [DocTypes.IncomeTaxReturn2019]: categoryIncome,
    [DocTypes.IncomeTaxReturn2020]: categoryIncome,
    [DocTypes.IncomeTaxReturn2021]: categoryIncome, // TODO: make dynamic
    [DocTypes.InitialClosingDisclosure]: categoryDisclosures,
    [DocTypes.InitialDisclosurePackage]: categoryDisclosures,
    [DocTypes.InitialLoanEstimate]: categoryDisclosures,
    [DocTypes.InvestorExceptionVarianceApproval]: categoryLoanDecision,
    [DocTypes.InvestorLoanApproval]: categoryLoanDecision,
    [DocTypes.InvestorPostclosingPackage]: categoryInvestor,
    [DocTypes.InvestorPrefundingPackage]: categoryInvestor,
    [DocTypes.InvestorRateLock]: categoryLoanDecision,
    [DocTypes.InvestorSuspenseConditions]: categoryInvestor,
    [DocTypes.K1]: categoryIncome,
    [DocTypes.LawsuitJudgment]: categoryClosing,
    [DocTypes.LetterOfExplanationAsset]: categoryAsset,
    [DocTypes.LetterOfExplanationCredit]: categoryClosing,
    [DocTypes.LetterOfExplanationIncome]: categoryIncome,
    [DocTypes.LoanDetailReport]: categoryPostClosing,
    [DocTypes.LoansafeAppraisalManagerReport]: categorySubjectProperty, // PDF
    [DocTypes.LoansafeAppraisalManagerXml]: categorySystemNotifications,
    [DocTypes.LoansafeFraudManagerReport]: categoryCredit, // PDF
    [DocTypes.LoansafeFraudManagerXml]: categorySystemNotifications,
    [DocTypes.ManuallyEditedDisclosure]: categoryDisclosures,
    [DocTypes.MarriageLicense]: categoryCredit,
    [DocTypes.MersRegistration]: categoryPostClosing,
    [DocTypes.MersTransfer]: categoryPostClosing,
    [DocTypes.MessageAttachment]: categoryMisc,
    [DocTypes.MortgageInsuranceCertificate]: categorySubjectProperty,
    [DocTypes.MortgageInsuranceQuote]: categorySubjectProperty,
    [DocTypes.MortgageInsuranceStatementOfDenial]: categorySubjectProperty,
    [DocTypes.MortgageStatement]: categoryPostClosing,
    [DocTypes.NonPermanentResidentVisa]: categoryCredit,
    [DocTypes.Note]: categoryClosing,
    [DocTypes.NoticeOfIncompleteApplication]: categoryLoanDecision,
    [DocTypes.OriginWarehouseTape]: categoryClosing,
    [DocTypes.PaymentFromBorrower]: categoryPostClosing,
    [DocTypes.PaymentHistoryStatement]: categoryPostClosing,
    [DocTypes.PayoffDemandStatement]: categoryClosing,
    [DocTypes.Paystub]: categoryIncome,
    [DocTypes.PermanentResidentCardVisa]: categoryCredit,
    [DocTypes.PestInspection]: categorySubjectProperty,
    [DocTypes.PhotoIdentification]: categoryCredit,
    [DocTypes.PlaidTransactionHistory]: categoryAsset,
    [DocTypes.PostClosingPayoffStatement]: categoryPostClosing,
    [DocTypes.PowerOfAttorney]: categoryClosing,
    [DocTypes.PreAppLoanEstimate]: categoryDisclosures,
    [DocTypes.ProfitAndLossStatement]: categoryIncome,
    [DocTypes.PurchaseAdvice]: categoryPostClosing,
    [DocTypes.PurchaseContract]: categorySubjectProperty,
    [DocTypes.RateLockAgreement]: categoryDisclosures,
    [DocTypes.RateLockAgreementCertificate]: categoryDisclosures,
    [DocTypes.RateLockTape]: categoryAccounting,
    [DocTypes.ReceiptOfFundsToClose]: categoryAsset,
    [DocTypes.RecordedSecurityInstrument]: categoryPostClosing,
    [DocTypes.RelocationAgreement]: categoryCredit,
    [DocTypes.RentalReoLease]: categoryIncome,
    [DocTypes.ReoAvm]: categoryCredit,
    [DocTypes.ReoEstimatedSettlementStatement]: categoryCredit,
    [DocTypes.ReoExpenses]: categoryCredit,
    [DocTypes.ReoFinalSettlementStatement]: categoryCredit,
    [DocTypes.ReoPendingSalePropertyContract]: categoryCredit,
    [DocTypes.RevisedLoanEstimate]: categoryDisclosures,
    [DocTypes.SatisfactionOfJudgementLienLawsuit]: categoryCredit,
    [DocTypes.SecurityInstrument]: categoryClosing,
    [DocTypes.ServiceLinkResponse]: categorySystemNotifications,
    [DocTypes.ServicingTape]: categoryPostClosing,
    [DocTypes.SettlementStatementEstimated]: categoryClosing,
    [DocTypes.SettlementStatementFinal]: categoryClosing,
    [DocTypes.ShippingLabel]: categoryPostClosing,
    [DocTypes.SocialSecurityCard]: categoryCredit,
    [DocTypes.SSNVerification]: categoryCredit,
    [DocTypes.Ssa89]: categoryDisclosures,
    [DocTypes.Statement]: categoryAsset,
    [DocTypes.SubjectPropertyAvm]: categorySubjectProperty,
    [DocTypes.SubjectPropertyOther]: categorySubjectProperty,
    [DocTypes.SubjectPropertyReport]: categorySubjectProperty,
    [DocTypes.TaxCertificate]: categorySubjectProperty,
    [DocTypes.TaxReturn1065Partnership]: categoryIncome,
    [DocTypes.TaxReturn1120CCorp]: categoryIncome,
    [DocTypes.TaxReturn1120sSCorp]: categoryIncome,
    [DocTypes.TaxReturnTranscript1065Partnership]: categoryIncome,
    [DocTypes.TaxReturnTranscript1120CCorp]: categoryIncome,
    [DocTypes.TaxReturnTranscript1120sSCorp]: categoryIncome,
    [DocTypes.TaxTranscript]: categoryIncome,
    [DocTypes.TitleCommitment]: categorySubjectProperty,
    [DocTypes.TitleEO]: categoryClosing,
    [DocTypes.TitleOther]: categorySubjectProperty,
    [DocTypes.TrustAgreementOrCertification]: categorySubjectProperty,
    [DocTypes.UCDFannie]: categoryPostClosing,
    [DocTypes.UCDFreddie]: categoryPostClosing,
    [DocTypes.ULIRegistration]: categoryPostClosing,
    [DocTypes.UnderwritingLoanApproval]: categoryLoanDecision,
    [DocTypes.UnderwritingSummary]: categoryLoanDecision,
    [DocTypes.Unknown]: categoryMisc,
    [DocTypes.Unused]: categoryMisc,
    [DocTypes.VACertificateOfEligibility]: categoryCredit, // TODO: Is this right?
    [DocTypes.VerbalVerificationOfEmployment]: categoryIncome,
    [DocTypes.VerificationOfMortgage]: categoryCredit,
    [DocTypes.VerificationOfRent]: categoryCredit,
    [DocTypes.W2]: categoryIncome,
    [DocTypes.W2Transcript]: categoryIncome,
    [DocTypes.W9]: categoryPostClosing,
    [DocTypes.WindAndHailPolicy]: categorySubjectProperty,
    [DocTypes.WireInstructions]: categoryClosing,
    [DocTypes.WorknumberEmploymentVerification]: categoryIncome,
    [DocTypes.WorknumberVerbalEmploymentVerification]: categoryIncome,
    [DocTypes.WrittenVerificationOfEmployment]: categoryIncome,
    [DocTypes.WvoeCertificate]: categoryIncome,
    [DocTypes.ChildCareLetter]: categoryCredit,
    [DocTypes.NPSCreditReport]: categoryCredit,
  };
  return map[t] || "";
};

export const docTypeToHumanStr = (t: DocTypes): string => {
  const map = {
    [DocTypes.TenOEightFinal]: "1008 FINAL",
    [DocTypes.TenOThreeFinal]: "1003 FINAL",
    [DocTypes.TenOThreeInitial]: "1003 INITIAL",
    [DocTypes.FortyFiveOSixTInitial]: "Form 4506-T",
    [DocTypes.FortyFiveOSixCInitial]: "Form 4506-C",
    [DocTypes.AcknowledgementOfCfpbBooklets]: "Acknowledgment of Receipt of Informational Booklets",
    [DocTypes.Appraisal1004dInvoice]: "Appraisal 1004d Invoice",
    [DocTypes.AppraisalSSRFannie]: "Appraisal SSR Fannie",
    [DocTypes.AppraisalSSRFreddie]: "Appraisal SSR Freddie",
    [DocTypes.AssetReport]: "Asset Report",
    [DocTypes.BankUnitedWarehouseFundingTape]: "BankUnited warehouse funding tape",
    [DocTypes.CharmBooklet]: "Consumer Handbook on Adjustable-Rate Mortgages",
    [DocTypes.ConditionalApprovalLetter]: "Conditional Approval Letter - PreQual",
    [DocTypes.CorrespondenceBorrower]: "Correspondence - Borrower",
    [DocTypes.CorrespondenceVendor_3rdPartyProvider]: "Correspondence - Vendor/3rd Party Provider",
    [DocTypes.CpaLetterBusinessLicense]: "CPA Letter / Business License",
    [DocTypes.DesktopUnderwriterFindings]: "Desktop Underwriter Findings",
    [DocTypes.DownpaymentAssistanceLetter]: "Down Payment Assistance",
    [DocTypes.FirstTimeHomebuyerEducationCertificate]: "First Time Homebuyer Education Certificate",

    // NOTE: jjw: GoodbyeLetterTransferOfServicing no longer selectable in UI; superseded by:
    // -- GoodbyeLetter
    // -- HOITransferOfServicing
    // type remains here until we choose to migrate older documents to two new types
    [DocTypes.GoodbyeLetterTransferOfServicing]: "Goodbye Letter / Transfer of Servicing",

    [DocTypes.HoaDues]: "HOA Dues",
    [DocTypes.HOITransferOfServicing]: "HOI Transfer of Servicing",
    [DocTypes.HomeLoanToolkitBooklet]: "Your Home Loan Toolkit",
    [DocTypes.Income]: "Income Unclassified - Tax Returns",
    [DocTypes.IncomeTaxReturn2013]: "Tax Return - 1040 Individual 2013",
    [DocTypes.IncomeTaxReturn2014]: "Tax Return - 1040 Individual 2014",
    [DocTypes.IncomeTaxReturn2015]: "Tax Return - 1040 Individual 2015",
    [DocTypes.IncomeTaxReturn2016]: "Tax Return - 1040 Individual 2016",
    [DocTypes.IncomeTaxReturn2017]: "Tax Return - 1040 Individual 2017",
    [DocTypes.IncomeTaxReturn2018]: "Tax Return - 1040 Individual 2018",
    [DocTypes.IncomeTaxReturn2019]: "Tax Return - 1040 Individual 2019",
    [DocTypes.IncomeTaxReturn2020]: "Tax Return - 1040 Individual 2020",
    [DocTypes.IncomeTaxReturn2021]: "Tax Return - 1040 Individual 2021", // TODO: make dynamic
    [DocTypes.InvestorExceptionVarianceApproval]: "Investor Exception / Variance Approval",
    [DocTypes.InvestorPostclosingPackage]: "Investor PostClosing Package",
    [DocTypes.K1]: "K-1",
    [DocTypes.LawsuitJudgment]: "Judgement / Lien / Lawsuit Documentation",
    [DocTypes.LetterOfExplanationAsset]: "Letter of Explanation - Asset",
    [DocTypes.LetterOfExplanationCredit]: "Letter of Explanation - Credit",
    [DocTypes.LetterOfExplanationIncome]: "Letter of Explanation - Income",
    [DocTypes.LoanDetailReport]: "Loan Detail Report",
    [DocTypes.LoansafeAppraisalManagerReport]: "LoanSafe Appraisal Manager Report",
    [DocTypes.LoansafeAppraisalManagerXml]: "LoanSafe Appraisal Manager XML",
    [DocTypes.LoansafeFraudManagerReport]: "LoanSafe Fraud Manager Report",
    [DocTypes.LoansafeFraudManagerXml]: "LoanSafe Fraud Manager XML",
    [DocTypes.MersRegistration]: "MERS Registration",
    [DocTypes.MersTransfer]: "MERS Transfer",
    [DocTypes.MortgageStatement]: "Mortgage Statement",
    [DocTypes.NonPermanentResidentVisa]: "Non-Permanent Resident Visa",
    [DocTypes.PaymentFromBorrower]: "Payment From Borrower",
    [DocTypes.PostClosingPayoffStatement]: "Post-Closing Payoff Statement",
    [DocTypes.PayoffDemandStatement]: "Payoff Demand/Statement",
    [DocTypes.PermanentResidentCardVisa]: "Permanent Resident Card / Visa",
    [DocTypes.PestInspection]: "Pest Inspection",
    [DocTypes.PhotoIdentification]: "Photo Identification",
    [DocTypes.PrequalPackageAuditTrail]: "Prequalification Package Audit Trail",
    [DocTypes.PrequalPackageCertificate]: "Prequalification Package Certificate",
    [DocTypes.ReceiptOfFundsToClose]: "Receipt Of Funds To Close",
    [DocTypes.RentalReoLease]: "Investment Property Lease",
    [DocTypes.ReoAvm]: "REO AVM",
    [DocTypes.ReoEstimatedSettlementStatement]: "REO Estimated Settlement Statement",
    [DocTypes.ReoFinalSettlementStatement]: "REO Final Settlement Statement",
    [DocTypes.SatisfactionOfJudgementLienLawsuit]: "Satisfaction of Judgement / Lien / Lawsuit",
    [DocTypes.ServiceLinkResponse]: "ServiceLink Response",
    [DocTypes.SettlementStatementEstimated]: "Settlement Statement - Estimated",
    [DocTypes.SettlementStatementFinal]: "Settlement Statement - Final",
    [DocTypes.Ssa89]: "Form SSA-89",
    [DocTypes.SSNVerification]: "SSN Verification",
    [DocTypes.Statement]: "Asset Statement",
    [DocTypes.SubjectPropertyAvm]: "Subject Property AVM",
    [DocTypes.SubjectPropertyOther]: "Subject Property - Other",
    [DocTypes.TaxReturnTranscript1065Partnership]: "Tax Return Transcript - 1065 Partnership",
    [DocTypes.TaxReturnTranscript1120CCorp]: "Tax Return Transcript - 1120 C-Corp",
    [DocTypes.TaxReturnTranscript1120sSCorp]: "Tax Return Transcript - 1120S S-Corp",
    [DocTypes.TaxTranscript]: "Tax Return Transcript - 1040 Individual",
    [DocTypes.TitleEO]: "Title E&O",
    [DocTypes.TrustAgreementOrCertification]: "Trust Agreement or Certification",
    [DocTypes.UCDFannie]: "UCD Fannie",
    [DocTypes.UCDFreddie]: "UCD Freddie",
    [DocTypes.ULIRegistration]: "ULI Registration",
    [DocTypes.UnderwritingSummary]: "Underwriting Summary",
    [DocTypes.VerificationOfMortgage]: "Verification of Mortgage",
    [DocTypes.VerificationOfRent]: "Verification of Rent",
    [DocTypes.W2]: "W-2",
    [DocTypes.W2Transcript]: "W-2 Transcript",
    [DocTypes.W9]: "W-9",
    [DocTypes.WorknumberEmploymentVerification]: "Worknumber Written Verification of Employment",
    [DocTypes.WorknumberVerbalEmploymentVerification]:
      "Worknumber Verbal Verification of Employment",
    [DocTypes.WvoeCertificate]: "WVOE audit trail",
    [DocTypes.ChildCareLetter]: "Child Care Letter",
    [DocTypes.NPSCreditReport]: "NPS Credit Report",
  };
  return map[t] || titleize(humanize(t));
};

export const docTypeSelectOpts: any = selectableDocTypes.map(t => ({
  value: t,
  label: docTypeToHumanStr(t),
}));

export const employmentToEmploymentTypeString = employment => {
  if (employment.income_type === "selfEmployed") {
    return "Self Employed";
  }
  if (employment.income_type === "salaried") {
    if (employment.employment_type === "fullTime") {
      return "Full-Time";
    }
    if (employment.employment_type === "partTime") {
      return "Part-Time";
    }
  }
};
